import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import {
  Button,
  DragDropFileList,
  FileListFileProps,
  Input,
  Label,
  Modal,
  Select,
  Text,
  WarningOutlineIcon,
} from '@mercai/clever'

import {
  api,
  ORDERS_CENTER_INVOICE_URL,
  QUERIES_ADMIN,
  useOrderCenterCommentsCreate,
} from '@/services'
import {
  getFileNameAndExtension,
  onErrorMessage,
  useValidationErrors,
} from '@/helpers'
import { schemaStep2 } from './modal-invoice-warning.constants'

import { SaleOrderCommentTypesEnum } from '@/types'
import { ModalInvoiceWarningProps } from './modal-invoice-warning.type'
import styles from './modal-invoice-warning.module.css'

export const ModalInvoiceWarning = ({
  saleOrderUuid = '',
  isOpen,
  invoice,
  onSaveAfterUpload,
  onClose,
}: ModalInvoiceWarningProps) => {
  const { t } = useTranslation('componentCardsOrderCenterShow')
  const queryClient = useQueryClient()

  const [step, setStep] = useState(0)
  const [step2Values, setStep2Values] = useState({
    reason: '',
    comment: '',
  })

  const [dataFilesInvoicePDF, setDataFilesInvoicePDF] = useState<
    FileListFileProps[]
  >([])

  const [dataFilesInvoiceXml, setDataFilesInvoiceXml] = useState<
    FileListFileProps[]
  >([])

  const [filesUploadedSuccessfully, setFilesUploadedSuccessfully] = useState({
    pdfFile: !!invoice?.pdfFile,
    xmlFile: !!invoice?.xmlFile,
  })

  const { mutateAsync, isPending } = useOrderCenterCommentsCreate(saleOrderUuid)

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schemaStep2(t),
    step2Values,
  )

  const onSubmit = async () => {
    const response = await mutateAsync({
      type: SaleOrderCommentTypesEnum.orderinvoice_file,
      subject: step2Values.reason,
      comment: step2Values.comment,
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrderUuid],
      })

      onClose()

      return
    }

    onErrorMessage(
      t('orderStatusesModalWarningSubmitError'),
      t('orderStatusesModalWarningSubmitErrorDescription'),
    )
  }

  const handleClickSubmit = () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)
    onSubmit()
  }

  const handleClickAdvanceStep = () => {
    if (
      filesUploadedSuccessfully.pdfFile &&
      filesUploadedSuccessfully.xmlFile
    ) {
      onSaveAfterUpload()
      return
    }

    setStep(1)
  }

  const handleClose = () => {
    setStep(0)
    onClose()
  }

  const steps = [
    {
      title: (
        <div className={styles['modal-header']}>
          <WarningOutlineIcon className={styles['header-icon']} />
          <Text variant="text-large-bold" className={styles.title}>
            <span>{t('orderStatusesModalInvoiceWarningTitle')}</span>
          </Text>
        </div>
      ),
      description: t('orderStatusesModalInvoiceWarningDescription'),
      component: (
        <div className={styles.component}>
          <div>
            <Label>
              {t('orderStatusesModalInvoiceWarningInvoicePdfLabel')}
            </Label>

            <DragDropFileList
              urlAdd={ORDERS_CENTER_INVOICE_URL(invoice?.uuid || '')}
              configsRequest={{
                fileField: 'pdf_file',
                method: 'PATCH',
              }}
              axiosInstance={api}
              data={dataFilesInvoicePDF}
              onChangeData={setDataFilesInvoicePDF}
              successedUpload={() =>
                setFilesUploadedSuccessfully((prev) => ({
                  ...prev,
                  pdfFile: true,
                }))
              }
              translations={{
                clickToUpload: t('uploadButton'),
                dragFile: t('uploadDrag'),
                sizeFile: t('uploadSize'),
              }}
            />
          </div>

          <div>
            <Label>
              {t('orderStatusesModalInvoiceWarningInvoiceXmlLabel')}
            </Label>
            <DragDropFileList
              urlAdd={ORDERS_CENTER_INVOICE_URL(invoice?.uuid || '')}
              configsRequest={{
                fileField: 'xml_file',
                method: 'PATCH',
              }}
              axiosInstance={api}
              data={dataFilesInvoiceXml}
              onChangeData={setDataFilesInvoiceXml}
              successedUpload={() =>
                setFilesUploadedSuccessfully((prev) => ({
                  ...prev,
                  xmlFile: true,
                }))
              }
              translations={{
                clickToUpload: t('uploadButton'),
                dragFile: t('uploadDrag'),
                sizeFile: t('uploadSize'),
              }}
            />
          </div>

          <div className={styles.footer}>
            <Button variant="secondary" onClick={handleClose}>
              {t('orderStatusesModalInvoiceWarningCancelButton')}
            </Button>

            <Button variant="primary" onClick={handleClickAdvanceStep}>
              {t('orderStatusesModalInvoiceWarningContinueButton')}
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className={styles['modal-header']}>
          <WarningOutlineIcon className={styles['header-icon']} />
          <Text variant="text-large-bold" className={styles.title}>
            <span>{t('orderStatusesModalInvoiceWarningStep2Title')}</span>
          </Text>
        </div>
      ),
      description: t('orderStatusesModalInvoiceWarningStep2Description'),
      component: (
        <div className={styles.component}>
          <div>
            <Label required>
              {t('orderStatusesModalInvoiceWarningReasonLabel')}
            </Label>
            <Select
              placeholder={t(
                'orderStatusesModalInvoiceWarningReasonPlaceholder',
              )}
              options={[
                {
                  label: t('orderStatusesModalInvoiceWarningReasonOption1'),
                  value: t('orderStatusesModalInvoiceWarningReasonOption1'),
                },
                {
                  label: t('orderStatusesModalInvoiceWarningReasonOption2'),
                  value: t('orderStatusesModalInvoiceWarningReasonOption2'),
                },
              ]}
              value={step2Values.reason}
              onChange={(reason) =>
                setStep2Values((prev) => ({ ...prev, reason }))
              }
              error={errors?.reason as string}
            />
          </div>

          <div>
            <Label>{t('orderStatusesModalInvoiceWarningCommentLabel')}</Label>
            <Input
              isTextArea={true}
              value={step2Values.comment}
              onChange={(comment) =>
                setStep2Values((prev) => ({ ...prev, comment }))
              }
              error={errors?.comment as string}
            />
          </div>

          <div className={styles.footer}>
            <Button variant="secondary" onClick={handleClose}>
              {t('orderStatusesModalInvoiceWarningCancelButton')}
            </Button>

            <Button
              variant="primary"
              onClick={handleClickSubmit}
              isActive={!isPending}
              disabled={isPending}
            >
              {t('orderStatusesModalInvoiceWarningSaveButton')}
            </Button>
          </div>
        </div>
      ),
    },
  ]

  useEffect(() => {
    if (invoice?.pdfFile) {
      const file = getFileNameAndExtension(invoice?.pdfFile || '')

      setDataFilesInvoicePDF([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: invoice?.pdfFile,
          uploaded: true,
        },
      ])
    } else {
      setDataFilesInvoicePDF([])
    }

    if (invoice?.xmlFile) {
      const file = getFileNameAndExtension(invoice?.xmlFile || '')

      setDataFilesInvoiceXml([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: invoice?.xmlFile,
          uploaded: true,
        },
      ])
    } else {
      setDataFilesInvoiceXml([])
    }
  }, [invoice])

  return (
    <Modal.Root isOpen={isOpen} onHandleOpen={handleClose} size="small">
      <Modal.Header
        title={steps?.[step].title || ''}
        description={steps?.[step].description || ''}
        className={styles.header}
      />
      <Modal.Body className={styles.body}>{steps?.[step].component}</Modal.Body>
    </Modal.Root>
  )
}
